import type { WorkoutLibraryRow } from '../types/library';

/**
 * Defensive defaults for `focusTags` / `equipmentTags` on a workout summary row.
 * Older sessionStorage caches predate the cream-redesign fields; without this, a
 * cached page render would crash before the server response replaces it.
 */
export function normalizeWorkoutSummaryRow<T extends WorkoutLibraryRow>(row: T): T {
  if (!row || typeof row !== 'object') return row;
  if (Array.isArray(row.focusTags) && Array.isArray(row.equipmentTags)) return row;
  return {
    ...row,
    focusTags: Array.isArray(row.focusTags) ? row.focusTags : [],
    equipmentTags: Array.isArray(row.equipmentTags) ? row.equipmentTags : [],
  };
}

/** Distinct exercise movements (unique exercise_id) across all blocks — not set/rep row count. */
export function countDistinctExerciseIdsInWorkoutBlocks(workoutBlocks: any[] | undefined): number {
  if (!workoutBlocks?.length) return 0;
  const ids = new Set<string>();
  for (const block of workoutBlocks) {
    for (const set of block.exerciseSets ?? []) {
      if (set.exerciseId) ids.add(set.exerciseId);
    }
  }
  return ids.size;
}

/** True when a library row is owned by the signed-in trainer (vs Bravos discover catalog seed). */
export function isTrainerOwnedLibraryRow(
  createdBy: string | null | undefined,
  trainerId: string | undefined,
): boolean {
  return !!trainerId && createdBy === trainerId;
}

/** Exercise count: distinct exercises (summary from server or local blocks), not set rows. */
export function getWorkoutExerciseCount(workout: {
  exerciseCount?: number;
  workoutBlocks?: { exerciseSets?: { exerciseId?: string }[] }[];
}): number {
  if (typeof workout.exerciseCount === 'number') return workout.exerciseCount;
  return countDistinctExerciseIdsInWorkoutBlocks(workout.workoutBlocks);
}
